import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
} from "react";
import {
  Link as AriaLink,
  Tab as AriaTab,
  type LinkProps,
  type MenuItemProps,
  type TabProps,
} from "react-aria-components";

type AnchorRenderProps = Extract<
  Parameters<NonNullable<LinkProps["render"]>>[0],
  { href: string }
>;

/**
 * Renders the app's router link for a React Aria element that carries an
 * `href` — `app/routerAnchor.tsx` in both apps.
 */
export type RouterLinkRender = (props: AnchorRenderProps) => ReactElement;

const RouterLinkContext = createContext<RouterLinkRender | undefined>(
  undefined,
);

/**
 * Supplies the router link used by every React Aria link below it. This
 * replaces React Aria's `RouterProvider`, which resolved every `href` through
 * the router's `useHref` and so mangled absolute URLs; React Router's `Link`
 * handles absolute URLs, `basename`, and relative paths itself.
 *
 * Without this provider links still render — they just leave the SPA when
 * clicked — so each app mounts it at the root.
 */
export const RouterLinkProvider: FC<{
  render: RouterLinkRender;
  children: ReactNode;
}> = ({ render, children }) => (
  <RouterLinkContext.Provider value={render}>
    {children}
  </RouterLinkContext.Provider>
);

/**
 * The React Aria `render` prop for a `Link`, or `undefined` when no router is
 * mounted. React Aria hands the render function an `href` even when it is not
 * rendering an anchor, so the element kind is what decides, not the `href`.
 */
export function useLinkRender(): LinkProps["render"] {
  const render = useContext(RouterLinkContext);

  return useMemo(
    () =>
      render &&
      ((domProps) =>
        "href" in domProps ? render(domProps) : <span {...domProps} />),
    [render],
  );
}

/** {@link useLinkRender} for components that fall back to a `<div>`. */
export function useMenuItemRender(): MenuItemProps["render"] {
  const render = useContext(RouterLinkContext);

  return useMemo(
    () =>
      render &&
      ((domProps) =>
        "href" in domProps ? render(domProps) : <div {...domProps} />),
    [render],
  );
}

/**
 * React Aria's `Link`, client-side routed through the app's router.
 *
 * A disabled link gets no `render` prop: React Aria renders those as a
 * `<span>` yet still passes the `href` through, so rendering a router link
 * from the `href` alone would leave a disabled link navigable.
 */
export const Link: FC<LinkProps & RefAttributes<HTMLAnchorElement>> = (
  props,
) => {
  const render = useLinkRender();

  return <AriaLink {...props} render={props.isDisabled ? undefined : render} />;
};

/** {@link useMenuItemRender} for tabs, whose render props differ. */
function useTabRender(): TabProps["render"] {
  const render = useContext(RouterLinkContext);

  return useMemo(
    () =>
      render &&
      ((domProps) =>
        "href" in domProps ? render(domProps) : <div {...domProps} />),
    [render],
  );
}

/** React Aria's `Tab`, client-side routed through the app's router. */
export const Tab: FC<TabProps & RefAttributes<HTMLDivElement>> = (props) => {
  const render = useTabRender();

  return <AriaTab {...props} render={render} />;
};
